import React, { useEffect, useState } from "react";
import moment from "moment";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ChartData,
} from "chart.js";
import {
    computeBackgroundColor,
    computeLabelAndCount,
    getProductTypeForBarChartLabel,
    getSelectedValueFromEnum,
    serialize,
} from "../../../../helpers/utils/Utils";
import { useNavigate } from "react-router-dom";
import { useLazyExportProductDataQuery } from "../../../../features/order/productsApi";
import {useGetOrderBarChartMutation} from "../../../../app/utilityRequestsApi";

// Register Chart.js components
ChartJS.register(BarElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

interface OrderCountHistogramProps {
    period: any;
    customerId: any;
    creationDateFrom?: any;
    creationDateTo?: any;
    applySearch: any;
    setApplySearch: any;
    applyExport: any;
    setApplyExport: any;
    enums?: any
    utilData?: any
}

const OrderCountHistogram: React.FC<OrderCountHistogramProps> = ({
    period,
    customerId,
    creationDateFrom,
    creationDateTo,
    applySearch,
    setApplySearch,
    applyExport,
    enums,
    setApplyExport,
    utilData
}) => {



    const [response, setResponse] = useState<any>([]);
    const [data, setData] = useState<ChartData<"bar">>({
        labels: [],
        datasets: [],
    });

    const navigate: any = useNavigate();
    const [exportDataApi] = useLazyExportProductDataQuery();
    const [getOrderBarChart] = useGetOrderBarChartMutation();

    const handleBarClick = (event: any, elements: any) => {


        if (elements.length > 0) {
            // Retrieve the data for the clicked bar
            const clickedElement = elements[0];
            const index = clickedElement.index;

            const isLastBarClicked = index === response.length - 1;

            const createdDateTo = isLastBarClicked
                ? creationDateTo
                : new Date(response?.at(index + 1)?.zonedDateTime ?? "");
            if (!isLastBarClicked) {
                createdDateTo.setDate(createdDateTo.getDate() - 1);
            }

            navigate({
                pathname: "/orders",
                search: serialize({
                    "createdDateFrom": moment(response.at(index).zonedDateTime).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                    "createdDateTo": moment(createdDateTo).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                    "type": (clickedElement.datasetIndex === 0 || clickedElement.datasetIndex === 1) ? getSelectedValueFromEnum(enums, "TAX_") : ((clickedElement.datasetIndex === 2 || clickedElement.datasetIndex === 3) ? getSelectedValueFromEnum(enums, "HOA_") : getSelectedValueFromEnum(enums, "NTP")),

                }),
                state: {
                    detail: {
                        "createdDateFrom": moment(response.at(index).zonedDateTime).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                        "createdDateTo": moment(createdDateTo).format("yyyy-MM-DD HH:mm:ss").toUpperCase(),
                        "type": (clickedElement.datasetIndex === 0 || clickedElement.datasetIndex === 1) ? getSelectedValueFromEnum(enums, "TAX_") : ((clickedElement.datasetIndex === 2 || clickedElement.datasetIndex === 3) ? getSelectedValueFromEnum(enums, "HOA_") : getSelectedValueFromEnum(enums, "NTP")),
                    }
                }
            });




        }
    };

    const options: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                min: 0,
                ticks: {
                    stepSize: 10,
                },
                stacked: true,
            },
            x: {
                stacked: true,
            },
        },
        plugins: {
            title: {
                display: true,
                text: "Order Counts",
                color: "black",
                font: {
                    size: 34,
                },
                padding: {
                    top: 20,
                    bottom: 20,
                },
            },
            legend: {
                display: true,
                position: "top",
            },
        },
        onClick: handleBarClick,
        onHover: function (event: any, chartElement: any) {
            const target = event.native?.target || event?.target;
            if (target instanceof HTMLCanvasElement) {
                target.style.cursor = chartElement.length ? 'pointer' : 'default';
            }
        },
    };

    useEffect(() => {
        if ((applySearch || applyExport) && enums) {
            fetchData();
        }
    }, [applySearch, applyExport, enums]);


    const fetchData = async () => {
        const query: Record<string, string | boolean | null> = {
            period,
            customerId: utilData?.customerLevel === 'CUSTOMER' ? utilData?.customer_Id : customerId || null,
            createdDateFrom: creationDateFrom ? moment(creationDateFrom).format("YYYY-MM-DD HH:mm:ss").toUpperCase() : null,
            createdDateTo: creationDateTo ? moment(creationDateTo).format("YYYY-MM-DD HH:mm:ss").toUpperCase() : null,
            setExportBtn: applyExport,
        };

        Object.keys(query).forEach((key) => query[key] === null && delete query[key]);

        try {

            if (applyExport) {
                await exportDataApi(query);
                setApplyExport(false)
            }
            else {
                const apiData: any = await getOrderBarChart(query).unwrap();
                localStorage.setItem("orderCountHistogramSearchParams", JSON.stringify(query));
                setResponse(apiData);

                let datasets = ['Tax', 'Tax Update', 'HOA', 'HOA Update', 'NTP', 'NTP Update'].map(label => {
                    let productTypes = getProductTypeForBarChartLabel(label, enums);
                    return ({
                        label: label,
                        data: computeLabelAndCount(period, apiData, productTypes, label.includes('Update')),
                        backgroundColor: computeBackgroundColor(label),
                        borderWidth: 1
                    });
                });

                setData({
                    labels: computeLabelAndCount(period, apiData, [], false, true),
                    datasets: datasets,
                });
                setApplySearch(false);
            }
        } catch (error) {
            setApplySearch(false);
            console.error("Error fetching data:", error);
        }
    };




    return (
        <>
            <div style={{ width: '100%', height: '600px' }}>
                <Bar data={data} options={options} />
            </div>
        </>
    );
};

export default OrderCountHistogram;

